import { useState, useEffect, useMemo } from 'react';
import Modal from '../components/Modal';
import { useNavigate } from '../routing';
import { suppliersApi } from '../utils/api';
import { toast } from '../components/Toast';
import { track, EVENTS } from '../utils/analytics';
import Icon from '../components/Icon';
import { HqOnlyLock } from '../components/HqOnlyNotice';

// A ח.פ. inside Hebrew text: left-to-right and unbroken — "514-000-777" would
// otherwise flip around its dashes and wrap mid-number.
const Bid = ({ value }) => <bdi dir="ltr" style={{ whiteSpace: 'nowrap' }}>{value}</bdi>;

// Admin-only screen for consolidating duplicate suppliers. Two columns let
// the admin pick a "from" (source — will be deleted) and a "to" (target —
// survives). A preview pane shows how many documents / payments / catalog
// rows will be re-parented before the merge runs. Final confirmation guards
// against accidental clicks.
export default function MergeSuppliersPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [searchFrom, setSearchFrom] = useState('');
  const [searchTo, setSearchTo] = useState('');
  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Both suppliers carry a different ח.פ.: the target keeps its own unless the
  // admin, warned in the confirmation, ticks this.
  const [overwriteBid, setOverwriteBid] = useState(false);
  const [mergeHqOnly, setMergeHqOnly] = useState(1);
  const [visibilityImpact, setVisibilityImpact] = useState(null);

  useEffect(() => {
    suppliersApi.getAll({ include_empty: true }).then(r => setSuppliers(r.data));
  }, []);

  // Fetch preview whenever both sides are chosen — keeps the counts in sync
  // without forcing the admin to click an extra "calculate" button.
  useEffect(() => {
    setOverwriteBid(false);
    setMergeHqOnly(1);
    setVisibilityImpact(null);
    if (!fromId || !toId || fromId === toId) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    suppliersApi.mergePreview(fromId, toId)
      .then(r => setPreview(r.data))
      .catch(err => {
        toast(err.response?.data?.error || 'שגיאה בטעינת תצוגה מקדימה', 'error');
        setPreview(null);
      })
      .finally(() => setLoadingPreview(false));
  }, [fromId, toId]);

  useEffect(() => {
    if (!preview?.hq_only_conflict || mergeHqOnly !== 0) {
      setVisibilityImpact(null);
      return;
    }
    const hiddenSupplier = preview.from.hq_only ? preview.from : preview.to;
    let active = true;
    suppliersApi.hqOnlyImpact(hiddenSupplier.name)
      .then(({ data }) => { if (active) setVisibilityImpact(data); })
      .catch(err => {
        if (active) toast(err.response?.data?.error || 'שגיאה בטעינת השפעת החשיפה', 'error');
      });
    return () => { active = false; };
  }, [preview, mergeHqOnly]);

  const filteredFrom = useMemo(() => {
    const q = searchFrom.trim().toLowerCase();
    return suppliers.filter(s => !q || (s.name || '').toLowerCase().includes(q));
  }, [suppliers, searchFrom]);
  const filteredTo = useMemo(() => {
    const q = searchTo.trim().toLowerCase();
    return suppliers.filter(s => !q || (s.name || '').toLowerCase().includes(q));
  }, [suppliers, searchTo]);

  const fromSupplier = suppliers.find(s => s.id === fromId);
  const toSupplier   = suppliers.find(s => s.id === toId);
  const conflicts    = preview?.doc_num_conflicts || [];

  // Source-only data that will be lost on merge — target wins. We surface the
  // gap so the admin can copy the values to the target before merging if they
  // matter. Only flag fields where source has a value and target doesn't.
  const sourceOnly = (() => {
    if (!preview?.from || !preview?.to) return [];
    const gaps = [];
    const has = v => v != null && String(v).trim() !== '';
    if (has(preview.from.payment_terms)  && !has(preview.to.payment_terms))  gaps.push({ label: 'תנאי תשלום',  value: preview.from.payment_terms });
    if (has(preview.from.payment_method) && !has(preview.to.payment_method)) gaps.push({ label: 'אמצעי תשלום', value: preview.from.payment_method });
    if (has(preview.from.business_id)    && !has(preview.to.business_id))    gaps.push({ label: 'ח.פ.',        value: preview.from.business_id });
    return gaps;
  })();

  const handleMerge = async () => {
    setMerging(true);
    try {
      const r = await suppliersApi.merge(fromId, toId, {
        overwriteBusinessId: overwriteBid,
        hqOnly: preview?.hq_only_conflict ? mergeHqOnly : undefined,
      });
      track(EVENTS.SUPPLIERS_MERGED, {
        documents_moved: r.data.documents_moved,
        payments_moved: r.data.payments_moved,
        credits_moved: r.data.credits_moved,
        catalog_moved: r.data.catalog_moved,
      });
      toast(`הספקים מוזגו: ${r.data.documents_moved} מסמכים, ${r.data.payments_moved} תשלומים, ${r.data.credits_moved} זיכויים, ${r.data.catalog_moved} פריטי קטלוג`);
      navigate(`/supplier/${toId}`);
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה במיזוג', 'error');
    } finally {
      setMerging(false);
      setConfirmOpen(false);
    }
  };

  const renderList = (items, selectedId, onSelect, otherSelectedId) => (
    <div style={{ border: '1px solid var(--line)', borderRadius: 12, maxHeight: 400, overflowY: 'auto' }}>
      {items.length === 0 ? (
        <div style={{ padding: 16, color: 'var(--ink-muted)', textAlign: 'center' }}>אין ספקים תואמים</div>
      ) : items.map(s => {
        const isSelected = s.id === selectedId;
        const isDisabled = s.id === otherSelectedId;
        return (
          <div
            key={s.id}
            onClick={() => !isDisabled && onSelect(s.id)}
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid var(--hairline)',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              background: isSelected ? 'var(--selected-soft-bg)' : (isDisabled ? 'var(--tint)' : '#fff'),
              color: isDisabled ? 'var(--ink-faint)' : 'var(--ink)',
              fontWeight: isSelected ? 700 : 500,
              fontSize: '0.9rem',
              userSelect: 'none',
            }}
          >
            {s.name}
            {/* Which side is hidden decides the visibility question the
                confirmation asks — show it before the admin picks. */}
            {' '}<HqOnlyLock hqOnly={s.hq_only} />
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <h1 className="page-title">איחוד ספקים</h1>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: '0.92rem', color: 'var(--ink-muted)', lineHeight: 1.6 }}>
          בוחרים <strong>ספק מקור</strong> (יימחק) ו<strong>ספק יעד</strong> (ישרוד). כל המסמכים, התשלומים, הזיכויים ופריטי הקטלוג של ספק המקור יועברו לספק היעד, השם שלו ושמות הכינוי שלו יתווספו לשמות הכינוי של היעד, ואז ספק המקור יימחק. <strong style={{ color: 'var(--danger-ink)' }}>פעולה זו אינה הפיכה.</strong>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--danger-ink)', marginTop: 0, marginBottom: 12 }}>
            ספק מקור (יימחק)
          </h3>
          <input
            className="form-control"
            placeholder="חיפוש..."
            value={searchFrom}
            onChange={e => setSearchFrom(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          {renderList(filteredFrom, fromId, setFromId, toId)}
          {fromSupplier && (
            <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
              נבחר: <strong>{fromSupplier.name}</strong>
            </div>
          )}
        </div>

        <div className="card">
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--success-ink)', marginTop: 0, marginBottom: 12 }}>
            ספק יעד (ישרוד)
          </h3>
          <input
            className="form-control"
            placeholder="חיפוש..."
            value={searchTo}
            onChange={e => setSearchTo(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          {renderList(filteredTo, toId, setToId, fromId)}
          {toSupplier && (
            <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
              נבחר: <strong>{toSupplier.name}</strong>
            </div>
          )}
        </div>
      </div>

      {fromId && toId && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--ink)', marginTop: 0, marginBottom: 12 }}>
            תצוגה מקדימה
          </h3>
          {loadingPreview ? (
            <div style={{ color: 'var(--ink-muted)' }}>טוען...</div>
          ) : preview ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                {[
                  { label: 'מסמכים',         value: preview.docs },
                  { label: 'תשלומים',       value: preview.payments },
                  { label: 'זיכויים',       value: preview.credits },
                  { label: 'פריטי קטלוג',   value: preview.catalog },
                  { label: 'עם מחיר קבוע',  value: preview.fixed_priced_count || 0 },
                ].map(b => (
                  <div key={b.label} style={{ background: 'var(--tint)', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginBottom: 4 }}>{b.label}</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--ink)' }}>{b.value}</div>
                  </div>
                ))}
              </div>

              {sourceOnly.length > 0 && (
                <div style={{ marginTop: 16, background: 'var(--success-bg)', border: '1px solid var(--success-line)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, color: 'var(--success-ink)', marginBottom: 6, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="info" size={15} /> שדות שיועברו אוטומטית מהמקור ליעד
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--success-ink)', lineHeight: 1.6 }}>
                    ספק היעד <strong>{toSupplier?.name}</strong> ריק בשדות הבאים — הם יועתקו מהמקור במהלך המיזוג:
                    <ul style={{ margin: '6px 0 0 0', paddingInlineStart: 20 }}>
                      {sourceOnly.map(g => (
                        <li key={g.label}><strong>{g.label}:</strong> {g.value}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {preview.business_id_conflict && (
                <div style={{ marginTop: 16, background: 'var(--warning-bg)', border: '1px solid var(--warning-line)', borderRadius: 10, padding: '12px 14px', fontSize: '0.85rem', color: 'var(--warning-ink)', lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="alert" size={15} /> לשני הספקים ח.פ. שונה
                  </div>
                  מקור: <strong><Bid value={preview.from.business_id} /></strong> · יעד: <strong><Bid value={preview.to.business_id} /></strong>.
                  {' '}ספק היעד ישמור את הח.פ. שלו, אלא אם תבחרי בחלון האישור להעתיק את של המקור.
                </div>
              )}

              {conflicts.length > 0 && (
                <div style={{ marginTop: 16, background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, color: 'var(--danger-ink)', marginBottom: 6, fontSize: '0.9rem' }}>
                    <Icon name="alert" size={15} /> נמצאו {conflicts.length} מסמכים עם מספרי מסמך כפולים בשני הספקים
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--danger-ink)', lineHeight: 1.5 }}>
                    לא ניתן למזג עד שתפתרי את הכפילויות (מחיקה / שינוי מספר). מספרי מסמך:
                    <div style={{ marginTop: 4, fontFamily: 'monospace', color: 'var(--danger-ink)' }}>
                      {conflicts.map(c => c.doc_num).join(', ')}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  className="btn btn-danger"
                  disabled={conflicts.length > 0 || merging}
                  onClick={() => setConfirmOpen(true)}
                  title={conflicts.length > 0 ? 'יש לפתור כפילויות מספרי מסמך תחילה' : ''}
                >
                  מזג ספקים
                </button>
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--ink-muted)' }}>לא ניתן לטעון תצוגה מקדימה</div>
          )}
        </div>
      )}

      {confirmOpen && (
        <Modal
          onClose={() => !merging && setConfirmOpen(false)}
          size={460}
          ariaLabel="אישור מיזוג"
        >
          <h3 className="modal-title">אישור מיזוג</h3>
          <div style={{ fontSize: '0.92rem', color: 'var(--ink)', lineHeight: 1.7 }}>
            עומדים למזג את <strong style={{ color: 'var(--danger-ink)' }}>{fromSupplier?.name}</strong> אל <strong style={{ color: 'var(--success-ink)' }}>{toSupplier?.name}</strong>.
            <br /><br />
            <strong>{preview?.docs}</strong> מסמכים, <strong>{preview?.payments}</strong> תשלומים, <strong>{preview?.credits}</strong> זיכויים ו-<strong>{preview?.catalog}</strong> פריטי קטלוג יועברו לספק היעד.
            <br /><br />
            <span style={{ color: 'var(--danger-ink)', fontWeight: 700 }}>ספק המקור יימחק. פעולה זו אינה הפיכה.</span>
          </div>
          {preview?.business_id_conflict && (
            <div style={{ marginTop: 14, background: 'var(--warning-bg)', border: '1px solid var(--warning-line)', borderRadius: 10, padding: '10px 12px', fontSize: '0.88rem', color: 'var(--warning-ink)' }}>
              <div style={{ marginBottom: 6 }}>
                <Icon name="alert" size={14} /> לשני הספקים ח.פ. שונה — מקור <Bid value={preview.from.business_id} />, יעד <Bid value={preview.to.business_id} />.
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={overwriteBid} onChange={e => setOverwriteBid(e.target.checked)} disabled={merging} />
                <span>להעתיק בכל זאת את הח.פ. של המקור (<Bid value={preview.from.business_id} />) ליעד</span>
              </label>
            </div>
          )}
          {preview?.hq_only_conflict && (
            <fieldset style={{ marginTop: 14, border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px' }}>
              <legend style={{ fontWeight: 700, paddingInline: 4 }}>האם הספק המאוחד יהיה מוסתר ממנהלים וצופים?</legend>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
                <input
                  type="radio"
                  name="merged-supplier-visibility"
                  value="1"
                  checked={mergeHqOnly === 1}
                  onChange={() => setMergeHqOnly(1)}
                  disabled={merging}
                />
                <span>בעלים/מטה בלבד</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
                <input
                  type="radio"
                  name="merged-supplier-visibility"
                  value="0"
                  checked={mergeHqOnly === 0}
                  onChange={() => setMergeHqOnly(0)}
                  disabled={merging}
                />
                <span>גלוי לכולם</span>
              </label>
              {mergeHqOnly === 0 && visibilityImpact && (
                <div className="hq-only-merge-impact">
                  כל ההיסטוריה של {preview.from.hq_only ? preview.from.name : preview.to.name} תוצג מיד ל: {visibilityImpact.hidden.map(person => person.email).join(', ') || 'אין'} (מנהלים, צופים).
                </div>
              )}
            </fieldset>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setConfirmOpen(false)} disabled={merging}>ביטול</button>
            <button className="btn btn-danger" onClick={handleMerge} disabled={merging}>
              {merging ? 'ממזג...' : 'כן, מזג'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
